import { ENDPOINTS, getFullUrl } from "./config";
import { DoctorFormData } from "./types";
import { DoctorDetailsType } from "../../types/doctor";
import {
  ICreateDoctorPrescriptionDataDTO,
  IUpdateDoctorPrescriptionDataDTO,
} from "../../types/prescription";

export class DoctorService {
  static getToken(): string | null {
    return localStorage.getItem("token");
  }
  static getRole(): string | null {
    return localStorage.getItem("auth_user");
  }

  static async addNewDoctor(doctorData: FormData) {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.DOCTOR.ADD), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: doctorData,
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Login failed",
      };
    }
  }

  // Update Doctor Details
  static async updateDoctor(
    doctorId: string,
    updatedData: Partial<DoctorFormData> | FormData,
  ) {
    console.log("new updated data", updatedData);

    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.DOCTOR.UPDATE.replace(":doctorId", doctorId)),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: updatedData as any,
        },
      );

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to update doctor",
      };
    }
  }

  // Phase 1: Update Appointment Setting (Clinic details & Consultation Fee)
  static async updateAppointmentSetting(data: any) {
    try {
      const token = this.getToken();
      if (!token) {
        return {
          success: false,
          message: "No authentication token found",
        };
      }

      const response = await fetch(
        getFullUrl(ENDPOINTS.DOCTOR.UPDATEAPPOINTMENTSETTING),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(data),
        },
      );

      let resData: any = null;
      try {
        resData = await response.json();
      } catch {
        resData = null;
      }

      if (!response.ok || resData?.success === false) {
        return {
          success: false,
          message:
            resData?.message ||
            resData?.error ||
            `Failed to update appointment setting (${response.status})`,
        };
      }

      return resData || { success: true };
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update appointment setting",
      };
    }
  }

  // Phase 2: Update Time Frame (Working hours, clash checks, slot regen, reschedule)
  static async updateTimeFrame(data: any) {
    try {
      const token = this.getToken();
      if (!token) {
        return {
          success: false,
          message: "No authentication token found",
        };
      }

      const response = await fetch(
        getFullUrl(ENDPOINTS.DOCTOR.UPDATENEWDOCTORPREFERENCES),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(data),
        },
      );

      let resData: any = null;
      try {
        resData = await response.json();
      } catch {
        resData = null;
      }

      if (!response.ok || resData?.success === false) {
        return {
          success: false,
          message:
            resData?.message ||
            resData?.error ||
            `Failed to update time frame (${response.status})`,
        };
      }

      return resData || { success: true };
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update time frame",
      };
    }
  }

  // Update Doctor Image
  static async DeleteDoctorImage(
    doctorId: string,
    updatedData: Partial<DoctorFormData>,
  ) {
    try {
      const response = await fetch(
        getFullUrl(
          ENDPOINTS.DOCTOR.DELETEPROFILEIMAGE.replace(":doctorId", doctorId),
        ),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: updatedData,
        },
      );

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to update doctor",
      };
    }
  }

  // Delete Doctor
  static async deleteDoctor(doctorId: string) {
    try {
      const url = `${getFullUrl(
        ENDPOINTS.DOCTOR.DELETE,
      )}?id=${doctorId}&role=Doctor`;
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to delete doctor",
      };
    }
  }

  static async getSpecializationDropdown() {
    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.DOCTOR.SPECIALIZATIONDROPDOWN),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      );

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch supervisors",
      };
    }
  }

  static async getDoctors({
    page = 1,
    pageSize = 10,
    searchText = "",
    specialization,
  }: {
    page?: number;
    pageSize?: number;
    searchText?: string;
    specialization?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    data?: {
      dataList: any[]; // Changed from DoctorDetailsType[] to any[] to accommodate new response structure temporarily or we should update the type
      totalItemsCount: number;
      totalPages: number;
      currentPage: number;
      nextPage: number | null;
      previousPage: number | null;
    };
  }> {
    try {
      const user: string = this.getRole();
      const url = new URL(getFullUrl(ENDPOINTS.DOCTOR.GETLIST));

      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());
      url.searchParams.append("searchText", searchText.toString());
      if (specialization) {
        url.searchParams.append("specialization", specialization.toString());
      }
      console.log("📡 API Request URL (Doctors):", url.toString());

      if (user && JSON.parse(user).role === "clinic") {
        url.searchParams.append("isdecommissioned", "false");
      }
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
          "Cache-Control": "no-cache",
        },
      });

      const data = await response.json();
      return {
        success: data.success,
        message: data.message,
        data: data.data, // Return data directly as provided in the new response format
      };
    } catch (error) {
      console.error(" Error in getDoctors:", error);
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to load doctors",
      };
    }
  }

  static async getDoctorAdminDetails(doctorId: string) {
    try {
      const response = await fetch(
        getFullUrl(
          ENDPOINTS.DOCTOR.GET_ADMIN_DOCTOR_DETAILS.replace(
            ":doctorId",
            doctorId,
          ),
        ),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to get Doctor Details");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(" Error in getDoctorAdminDetails:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch Doctor details",
      };
    }
  }

  static async getAppointmentData(doctorId: string) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.DOCTOR.APPOINTMENTDATA));
      //const url=new URL(`http://localhost:3002/doctorservice/doctor/fetch/appointmentDetails`)
      url.searchParams.append("doctorId", doctorId);
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const responseData = await response.json();
      return responseData.data;
    } catch (error) {
      console.error(" Error in getAppointmentData:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch appointment details",
      };
    }
  }
  static async getDoctorProfileById(doctorId: string) {
    try {
      const response = await fetch(
        `${getFullUrl(ENDPOINTS.DOCTOR.GETDOCTORBYID)}?doctorId=${doctorId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to get Doctor Details");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(" Error in getDoctorData:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch Doctor details",
      };
    }
  }

  static async getPatientHistoryByDate(doctorId: string, date: string) {
    try {
      const url = new URL(getFullUrl(ENDPOINTS.DOCTOR.PATIENTHISTORY));
      //const url=new URL (`http://localhost:3002/doctorservice/doctor/fetch/appointment/date`);
      url.searchParams.append("doctorId", doctorId);
      url.searchParams.append("date", date);
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
    }
  }

  static async markDoctorDecommissiond(decommissiondData: {
    doctorId: string;
    decommissioned: boolean;
  }) {
    try {
      const response = await fetch(getFullUrl(ENDPOINTS.DOCTOR.DECOMMISSION), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json", // Ensure JSON format
          Authorization: `Bearer ${this.getToken()}`,
        },
        body: JSON.stringify(decommissiondData),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : `Failed to Makr ${
                decommissiondData.decommissioned
                  ? "Decommissioned"
                  : "Commissioned"
              }`,
      };
    }
  }

  static async getNewDoctorPreferences(
    doctorId: string,
    isOnline: boolean,
    isSelfClinic: boolean,
    isInstant: boolean,
  ) {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        return null;
      }

      const url = new URL(
        getFullUrl(
          `${ENDPOINTS.DOCTOR.GETNEWDOCTORPREFERENCES}?userId=${doctorId}&isOnline=${isOnline}&isSelfClinic=${isSelfClinic}&isInstant=${isInstant}`,
        ),
      );

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.error(
          `Error fetching patient history: ${response.status} - ${response.statusText}`,
        );
        return null;
      }

      const result = await response.json();
      return result.success ? result.data : [];
    } catch (error) {
      console.error("Unexpected error in getNewDoctorPreferences:", error);
      return null;
    }
  }

  static async updateExistingDoctorPreferences(formData: any) {
    try {
      if (!formData?.id) {
        return {
          success: false,
          message: "Appointment setting ID is required",
        };
      }

      // 1. Prepare Phase 1 Payload (Appointment Setting: clinic info & consultation fee)
      const settingPayload: Record<string, any> = {
        id: formData.id,
      };
      if (
        formData.consultation_fee !== undefined &&
        formData.consultation_fee !== null &&
        formData.consultation_fee !== ""
      ) {
        const fee = Number(formData.consultation_fee);
        if (!isNaN(fee)) settingPayload.consultation_fee = fee;
      }
      if (formData.name !== undefined) settingPayload.name = formData.name;
      if (formData.address !== undefined)
        settingPayload.address = formData.address;
      if (formData.city !== undefined) settingPayload.city = formData.city;
      if (formData.state !== undefined) settingPayload.state = formData.state;
      if (formData.pincode !== undefined)
        settingPayload.pincode = formData.pincode;
      if (formData.mobile_number !== undefined)
        settingPayload.mobile_number = formData.mobile_number;
      else if (formData.mobileNumber !== undefined)
        settingPayload.mobile_number = formData.mobileNumber;

      if (formData.lat !== undefined || formData.latitude !== undefined) {
        const latVal =
          formData.latitude !== undefined
            ? Number(formData.latitude)
            : Number(formData.lat);
        if (!isNaN(latVal)) {
          settingPayload.lat = latVal;
          settingPayload.latitude = latVal;
        }
      }
      if (formData.long !== undefined || formData.longitude !== undefined) {
        const longVal =
          formData.longitude !== undefined
            ? Number(formData.longitude)
            : Number(formData.long);
        if (!isNaN(longVal)) {
          settingPayload.long = longVal;
          settingPayload.longitude = longVal;
        }
      }

      // 2. Prepare Phase 2 Payload (Time Frame: schedules, days, clash checks)
      const timeFramePayload: Record<string, any> = {
        id: formData.id,
      };
      if (formData.doctor_id !== undefined)
        timeFramePayload.doctor_id = formData.doctor_id;
      if (formData.clinic_id !== undefined)
        timeFramePayload.clinic_id = formData.clinic_id;
      if (formData.sunday !== undefined)
        timeFramePayload.sunday = Boolean(formData.sunday);
      if (formData.monday !== undefined)
        timeFramePayload.monday = Boolean(formData.monday);
      if (formData.tuesday !== undefined)
        timeFramePayload.tuesday = Boolean(formData.tuesday);
      if (formData.wednesday !== undefined)
        timeFramePayload.wednesday = Boolean(formData.wednesday);
      if (formData.thursday !== undefined)
        timeFramePayload.thursday = Boolean(formData.thursday);
      if (formData.friday !== undefined)
        timeFramePayload.friday = Boolean(formData.friday);
      if (formData.saturday !== undefined)
        timeFramePayload.saturday = Boolean(formData.saturday);
      if (formData.is_monthly !== undefined)
        timeFramePayload.is_monthly = Boolean(formData.is_monthly);
      if (formData.week_of_month !== undefined)
        timeFramePayload.week_of_month = Number(formData.week_of_month);

      if (formData.TimeFrame !== undefined) {
        timeFramePayload.TimeFrame = formData.TimeFrame.map((tf: any) => {
          const item: any = {
            appointment_setting_id: tf.appointment_setting_id || formData.id,
            day_of_week: tf.day_of_week,
            start_time: tf.start_time,
            end_time: tf.end_time,
            slot_duration: Number(tf.slot_duration),
          };
          if (
            tf.id !== undefined &&
            tf.id !== null &&
            !String(tf.id).includes("L")
          ) {
            const numId = Number(tf.id);
            if (!isNaN(numId)) {
              item.id = numId;
            }
          }
          return item;
        });
      }

      const hasSettingFields = Object.keys(settingPayload).length > 1; // More than just id
      const hasTimeFrameFields = Array.isArray(timeFramePayload.TimeFrame);

      let settingResult: any = null;
      let timeFrameResult: any = null;

      // Execute Phase 1 (Update Appointment Setting)
      if (hasSettingFields) {
        settingResult = await this.updateAppointmentSetting(settingPayload);
        if (!settingResult || settingResult.success === false) {
          return {
            success: false,
            message:
              settingResult?.message || "Failed to update appointment setting",
          };
        }
      }

      // Execute Phase 2 (Update Time Frame)
      if (hasTimeFrameFields) {
        timeFrameResult = await this.updateTimeFrame(timeFramePayload);
        if (!timeFrameResult || timeFrameResult.success === false) {
          return {
            success: false,
            message:
              timeFrameResult?.message || "Failed to update time frame",
          };
        }
      }

      return {
        success: true,
        data: timeFrameResult?.data || settingResult?.data,
        message:
          timeFrameResult?.message ||
          settingResult?.message ||
          "Doctor preferences updated successfully",
      };
    } catch (error) {
      console.error(
        "Unexpected error in updateExistingDoctorPreferences:",
        error,
      );
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Unexpected error while updating doctor preferences",
      };
    }
  }

  static async addNewAppointmentSetting(formData: any) {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        return null;
      }

      const url = new URL(
        getFullUrl(`${ENDPOINTS.DOCTOR.ADDNEWAPPOINTMENTSETTING}`),
      );
      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        console.error(
          `Error while adding new Appointment setting: ${response.status} - ${response.statusText}`,
        );
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(
        "Unexpected error in updateExistingDoctorPreferences",
        error,
      );
      throw error;
    }
  }

  static async deleteExistingTimeFrame(frameId: number, doctorId: string) {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        return null;
      }

      const url = new URL(
        getFullUrl(
          `${ENDPOINTS.DOCTOR.DELETETIMEFRAME}?frameId=${frameId}&doctorId=${doctorId}`,
        ),
      );
      const response = await fetch(url.toString(), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        console.error(
          `Error updating doctor Time frame: ${response.status} - ${response.statusText}`,
        );
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(
        "Unexpected error in updateExistingDoctorPreferences",
        error,
      );
      return null;
    }
  }

  static async fetchClinicListing(
    page: number,
    pageSize: number,
    searchText: string,
  ) {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        return null;
      }

      const url = new URL(
        getFullUrl(
          `${ENDPOINTS.DOCTOR.FETCHCLINICLISTING}?page=${page}&pageSize=${pageSize}&searchText=${searchText}`,
        ),
      );
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        console.error(
          `Error while fetching clinic listing: ${response.status} - ${response.statusText}`,
        );
        return null;
      }

      return await response.json();
    } catch (error) {
      return [];
    }
  }

  static async addNewSlots(data: any): Promise<any> {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        return null;
      }

      const url = new URL(
        getFullUrl(`${ENDPOINTS.DOCTOR.ADDNEWDOCTORPREFERENCES}`),
      );
      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        console.error(
          `Error while adding new slots: ${response.status} - ${response.statusText}`,
        );
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error("Unexpected error in addNewSlots", error);
      throw error;
    }
  }
  static async getMapDetails(url: string) {
    try {
      const response = await fetch(
        getFullUrl(
          `${ENDPOINTS.DOCTOR.MAPDETAILS}?url=${encodeURIComponent(url)}`,
        ),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch map details",
      };
    }
  }

  static async getClinicDoctorListing({
    specialization,
    name,
    page = 1,
    pageSize = 10,
  }: {
    specialization?: string;
    name?: string;
    page?: number;
    pageSize?: number;
  }) {
    try {
      const url = new URL(
        getFullUrl(ENDPOINTS.DOCTOR.CLINIC_DOCTOR_LISTING),
      );
      if (specialization) url.searchParams.append("specialization", specialization);
      if (name) url.searchParams.append("name", name);
      url.searchParams.append("page", page.toString());
      url.searchParams.append("pageSize", pageSize.toString());

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          "Content-Type": "application/json",
        },
      });
      return await response.json();
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch clinic doctor listing",
      };
    }
  }

  // ── Doctor Prescription Data APIs ───────────────────────────────────────────

  static async getDoctorPrescriptionData(doctorId: string) {
    try {
      const response = await fetch(
        getFullUrl(
          ENDPOINTS.DOCTOR.PRESCRIPTION_DATA_BY_ID.replace(":doctorId", doctorId),
        ),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      if (!response.ok) {
        return {
          success: false,
          notFound: response.status === 404,
          status: response.status,
          message: data?.message || "Doctor prescription data not found",
        };
      }
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch doctor prescription data",
      };
    }
  }

  static async createDoctorPrescriptionData(
    payload: ICreateDoctorPrescriptionDataDTO,
  ) {
    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.DOCTOR.PRESCRIPTION_DATA),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to create doctor prescription data",
      };
    }
  }

  static async updateDoctorPrescriptionData(
    doctorId: string,
    payload: IUpdateDoctorPrescriptionDataDTO,
  ) {
    try {
      const response = await fetch(
        getFullUrl(
          ENDPOINTS.DOCTOR.PRESCRIPTION_DATA_BY_ID.replace(":doctorId", doctorId),
        ),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update doctor prescription data",
      };
    }
  }

  static async deleteDoctorPrescriptionData(doctorId: string) {
    try {
      const response = await fetch(
        getFullUrl(
          ENDPOINTS.DOCTOR.PRESCRIPTION_DATA_BY_ID.replace(":doctorId", doctorId),
        ),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete doctor prescription data",
      };
    }
  }
}


